import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Cloud, Shield, FileText, CheckCircle2, TrendingUp, Clock, Users } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'Microsoft Power BI', icon: null, image: '/download.jpg' },
  { name: 'Microsoft PowerApps', icon: null, image: '/microsoft_powerapps.jpg' },
  { name: 'Microsoft Power Automate', icon: null, image: '/microsoft_powerautomate.jpg' },
  { name: 'Microsoft Azure', icon: null, image: '/microsoft_Azure.jpg' },
  { name: 'Microsoft SharePoint', icon: null, image: '/microsoft_sharepoint.png' }
];

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '95%',
    label: 'Workflow Automation',
    description: 'Manual processes eliminated through intelligent automation'
  },
  {
    icon: Clock,
    value: '1 hour',
    label: 'Reporting Time',
    description: 'Reduced from 5 days to under 1 hour'
  },
  {
    icon: Users,
    value: '20+',
    label: 'Hours Saved Weekly',
    description: 'Freed for strategic analysis across organization'
  },
  {
    icon: CheckCircle2,
    value: '99%',
    label: 'Data Accuracy',
    description: 'Through automated validation and security controls'
  }
];

const highlights = [
  {
    category: 'Operational Efficiency',
    items: [
      'Automated data processing for 95% of manual workflows, eliminating human error and accelerating operations',
      'Reduced executive reporting time from 5 days to under 1 hour through real-time dashboard implementation',
      'Freed up 20+ hours weekly across the organization for strategic analysis instead of manual data management'
    ]
  },
  {
    category: 'Data Quality & Compliance',
    items: [
      'Improved data accuracy to 99% with automated validation and row-level security controls',
      'Enhanced operational compliance by 85% through automated audit trails and regulatory reporting',
      'Achieved real-time visibility across all 50+ branch locations with centralized dashboard monitoring'
    ]
  }
];

const challenges = [
  {
    title: 'Fragmented Data Management',
    icon: Database,
    problem: 'Branch locations were using separate spreadsheets and disconnected reporting systems, making centralized oversight impossible while maintaining branch autonomy.',
    solution: 'Our Power Platform specialists implemented a unified data architecture using Azure Data Pipelines and Power BI dashboards. We created branch-specific views with row-level security ensuring each location could access only their relevant data while providing executives with comprehensive organizational insights.',
    impact: 'Unified data across 50+ branch locations'
  },
  {
    title: 'Manual Document Processing',
    icon: FileText,
    problem: 'Supplier communications and service documentation required time-consuming manual sorting, filing, and processing across multiple departments.',
    solution: 'We deployed intelligent Power Automate workflows that automatically process incoming emails, classify documents based on content analysis, and route files to appropriate SharePoint repositories. This eliminated manual document handling and ensured consistent filing protocols.',
    impact: 'Eliminated manual document processing'
  },
  {
    title: 'Inefficient Field Data Collection',
    icon: Cloud,
    problem: 'Service technicians were using paper-based forms for documentation, leading to data entry delays, transcription errors, and incomplete records.',
    solution: 'Using PowerApps, we developed mobile-first applications with integrated image capture, offline synchronization, and real-time data validation. Field teams can now document services immediately with photographic evidence and automatic data submission upon connectivity.',
    impact: 'Real-time field data collection implemented'
  },
  {
    title: 'Compliance and Security Gaps',
    icon: Shield,
    problem: 'Ensuring regulatory compliance and data security across multiple locations was challenging with inconsistent access controls and audit trail gaps.',
    solution: 'We implemented enterprise-grade security protocols with row-level access controls, automated compliance monitoring, and comprehensive audit trail generation. All data access is now logged and monitored in real-time with automated compliance reporting.',
    impact: '85% improvement in operational compliance'
  }
];

const RetailAnalytics = () => {
  return (
    <>
      <SEOHead
        title="Retail Analytics Case Study | Agility"
        description="AI-driven retail analytics improving demand forecasting and inventory management."
        url="https://www.agilitytech.ai/case-studies/retail-analytics"
      />
    <div className="pt-20 bg-black">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80"
            alt="Retail Analytics Banner"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
              <span className="text-teal-400 text-sm font-medium">Healthcare/Retail Pharmacy</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Power Platform Utilisation - Retail Analytics
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
              A comprehensive digital transformation for a leading UK retail pharmacy network, 
              streamlining operations across 50+ branches through intelligent automation and 
              real-time analytics.
            </p>
          </div>
        </div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyMetrics.map((metric, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 
                border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <metric.icon className="h-6 w-6 text-teal-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                <p className="text-gray-400 text-sm">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3>
              <p className="text-white">Healthcare/Retail Pharmacy</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3>
              <p className="text-white">United Kingdom</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3>
              <p className="text-white">Enterprise-Level Implementation</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3>
              <p className="text-white">Q2 2024 - Completed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-12 bg-gray-900 border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
          <div className="flex flex-wrap justify-center gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
                {tech.image ? (
                  <img 
                    src={tech.image} 
                    alt={tech.name}
                    className="h-6 w-6 object-contain"
                  />
                ) : (
                  <tech.icon className="h-6 w-6 text-teal-400" />
                )}
                <span className="text-gray-300 font-medium">{tech.name}</span>
              </div>
            ))}
            <div className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
              <Shield className="h-6 w-6 text-teal-400" />
              <span className="text-gray-300 font-medium">Enterprise Security</span>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                  <Database className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Fragmented Operations Across 50+ Locations</h3>
                  <p className="text-xl text-gray-300 leading-relaxed">
                    A leading UK retail pharmacy network serving 200,000+ patients across multiple facilities 
                    struggled with fragmented data management, manual document processing, and inefficient 
                    field operations. Branch locations operated independently with separate spreadsheets and 
                    disconnected systems, making centralized oversight nearly impossible while maintaining 
                    operational efficiency and regulatory compliance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Transformational Results</h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
              <div className="flex items-start space-x-4 mb-8">
                <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle2 className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Unified Digital Transformation</h3>
                  <p className="text-xl text-gray-300 leading-relaxed">
                    We replaced the fragmented, manual workflow with a unified, secure pipeline that transformed 
                    operational efficiency across all 50+ branch locations. By implementing Microsoft Power Platform 
                    solutions with enterprise-grade security and automation, we delivered measurable business impact 
                    that exceeded client expectations.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {highlights.map((category, index) => (
                <div key={index} className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30">
                  <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                  <div className="space-y-4">
                    {category.items.map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                        <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {challenges.map((challenge, index) => (
              <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 
                border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                    <challenge.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                      Problem
                    </h4>
                    <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                      Solution
                    </h4>
                    <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                  </div>
                  
                  <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                    <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                    <p className="text-teal-300 font-medium">{challenge.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Database className="h-8 w-8 text-teal-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Transform Your Operations?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Contact our Power Platform specialists to discover how intelligent automation and 
              unified data management can revolutionize your business operations.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 
                rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <span>Get Started Today</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default RetailAnalytics;